<template>
    <list-container fixed>
        <template #header>
            <list-cell-header :size="45">
                <template #title>
                    Bestandnaam
                </template>
            </list-cell-header>
            <list-cell-header :width="20">
                <template #title>
                    Omvang
                </template>
            </list-cell-header>
            <list-cell-header :width="25">
                <template #title>
                    Datum
                </template>
            </list-cell-header>
            <list-cell-header :width="10" />
        </template>
        <template #content>
            <list-row
                v-for="(document, index) in sortedDocuments"
                :key="document.id.value"
                :background-color="index % 2 ? 'gray-100' : 'white'"
            >
                <list-cell-text>
                    <template #content>
                        <text-decorator :size="FontSize.xs">
                            {{ document.fileName }}
                        </text-decorator>
                    </template>
                </list-cell-text>
                <list-cell-text>
                    <template #content>
                        <text-decorator v-if="document.fileSize" :size="FontSize.xs">
                            {{ (document.fileSize / 1024).toFixed(2) }} kB
                        </text-decorator>
                    </template>
                </list-cell-text>
                <list-cell-text>
                    <template #content>
                        <text-decorator v-if="document.createdAt" :size="FontSize.xs">
                            {{ formatDate(document.createdAt) }}
                        </text-decorator>
                    </template>
                </list-cell-text>
                <list-cell-text>
                    <template #content>
                        <button-plain-icon
                            :color="'red-500'"
                            :icon="IconDelete"
                            :size="IconSize.sm"
                            @click="openDeleteConfirmation(document)"
                        />
                    </template>
                </list-cell-text>
            </list-row>
            <confirmation-dialog
                ref="confirmDelete"
                label="Document verwijderen"
                description="Weet je zeker dat je dit document wilt verwijderen?"
            >
                <template #default>
                    <text-heading>Weet u dit zeker?</text-heading>
                    <p>
                        Weet u zeker dat u dit document wil verwijderen?
                        <br>Dit kan niet meer ongedaan worden!
                    </p>
                </template>
                <template #actions="{ bindings }">
                    <button-outline @click="bindings.overlay.closeOverlay();">
                        Annuleren
                    </button-outline>
                    <button-block
                        @loading="(event) => {
                            deleteSelectedDocument();
                            event.button.setState(ButtonState.content);
                            bindings.overlay.closeOverlay();
                        }"
                    >
                        Bevestig
                    </button-block>
                </template>
            </confirmation-dialog>
        </template>
    </list-container>
</template>

<script setup lang="ts">
    import {
        ButtonState,
        IconSize,
        type ExposedOverlay,
        ConfirmationDialog,
        FontSize,
    } from '@keyvue/one-ui';
    import IconDelete from '@keyvue/one-material-icons/icons/delete.svg?component';
    import type { AssistentDocument } from '@keyvue/one-api-client';
    import { computed, ref } from 'vue';
    import { formatDate } from '@/shared/utils/date';

    const props = defineProps<{ documents: AssistentDocument[] }>();
    const emit = defineEmits<{
        (e: 'delete:document', value: AssistentDocument): void;
    }>();

    const confirmDelete = ref<ExposedOverlay>();
    const deletingDocument = ref<AssistentDocument | null>();

    function openDeleteConfirmation(document: AssistentDocument) {
        deletingDocument.value = document;
        confirmDelete.value?.toggleOverlay();
    }

    async function deleteSelectedDocument() {
        if (!deletingDocument.value) {
            return;
        }

        emit('delete:document', deletingDocument.value);
        deletingDocument.value = null;
    }

    const sortedDocuments = computed(() => {
        return [...props.documents].sort((a, b) => {
            if (!a.fileName || !b.fileName) {
                return 0;
            }

            return a.fileName.localeCompare(b.fileName);
        });
    });
</script>
